<script setup lang="ts">
import { computed } from 'vue';

import { ColorSelectOption } from '@/classes/ColorSelectOption';
import type { SelectOption } from '@/classes/SelectOption';

type Props = {
  type: 'checkbox' | 'radio';
  option: SelectOption | ColorSelectOption;
  showInput?: boolean;
  checked: boolean;
  name?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'change': [checked: boolean, value: string, option: SelectOption | ColorSelectOption];
}>();

const colorValue = computed(() => {
  return props.option instanceof ColorSelectOption ? props.option.color : undefined;
});

function handleChange(event: Event) {
  const target = event.target as HTMLInputElement;
  emit('change', target.checked, props.option.value, props.option);
}
</script>

<template>
  <label class="choiceChip btn"
         :data-color="colorValue"
         :data-show-input="props.showInput ? 'true' : undefined">
    <input :type="type"
           class="choiceChip-input"
           :checked="checked"
           :value="option.value"
           :name="name"
           @change="handleChange" />
    <span class="choiceChip-text">{{ option.label }}</span>
  </label>
</template>

<style lang="scss">
.choiceChip {
  --baseColor: var(--primary-500);
  --buttonBackgroundColor: var(--baseColor);
  --lightnessModifierPressed: 1.25;
  --accentLightnessModifier: .5;
  --choiceChipBorderRadius: 120px;

  justify-content: start;
  border: 1px solid var(--gray-300);
  margin-bottom: 0;
  
  background-color: transparent;
  border-radius: var(--choiceChipBorderRadius);
  cursor: pointer;

  text-align: start;

  &:is(:hover, :focus-visible) {
    background-color: oklch(from var(--buttonBackgroundColor) calc(l * var(--lightnessModifierPressed)) c h / .1);
    border-color: var(--baseColor);
  }

  &:has(.choiceChip-input:checked) {
    background-color: oklch(from var(--buttonBackgroundColor) calc(l * var(--lightnessModifierPressed)) c h / .2);
    border-color: var(--baseColor);
    outline: 1px solid var(--baseColor);
  }

  &-input {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    accent-color: oklch(from var(--buttonBackgroundColor) calc(l * var(--accentLightnessModifier)) c h);

    .choiceChip[data-show-input='true'] & {
      position: static;
      opacity: 1;
    }
  }
}
</style>
